import type { BtlApplication } from 'api/btlApplicationForm/types';
import { ApplicationStatus, AddressHistory, IndividualCreateDto } from 'api/btlApplicationForm/types';
import { btlApplicationFormApi } from 'api/btlApplicationForm';
import { App } from 'antd';
import type { OoCaseSidebarContext } from 'components/CaseSidebar/buildOoCaseSidebar';
import { CaseSidebarItemId } from 'components/CaseSidebar/types';
import { FormValidationContext } from 'hooks';
import { individualFormSchema } from 'pages/BtlApplicationFormPage/Applicants/Individual/formSchema';
import { mapIndividualToFormValues } from 'pages/BtlApplicationFormPage/Applicants/Individual/mapIndividualToFormValues';
import { invalidateBtlApplicationData } from 'pages/BtlApplicationFormPage/btl-utils';
import { transformAddress } from 'pages/BtlApplicationFormPage/common/Address';
import { makeFormReadonly } from 'pages/BtlApplicationFormPage/makeFormReadonly';
import {
  normalizeIndividualIncomesForForm,
  mapFormIncomesPayloadToDto,
} from 'pages/BtlApplicationFormPage/TopSlicing/Household/mapIndividualIncomesToFormValues';
import { useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { config } from 'utils/config';
import { isValidGbPhoneNumber } from 'utils/gbPhoneNumber';
import { isOoApplicantIndividualFormEditable } from './CaseApplicantIndividualPage';

interface UseOoCaseApplicantIndividualFormParams {
  caseData?: BtlApplication;
  individualId?: string | null;
  isCreateMode?: boolean;
  isCaseLoading: boolean;
  sidebarContext: OoCaseSidebarContext;
}

export function useOoCaseApplicantIndividualForm({
  caseData,
  individualId,
  isCreateMode = false,
  isCaseLoading,
  sidebarContext,
}: UseOoCaseApplicantIndividualFormParams) {
  const { formatMessage } = useIntl();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const formValidationContext = useContext(FormValidationContext);
  const { env } = config;

  const individualData = individualId
    ? caseData?.borrower.individuals.find(individual => individual.id === individualId)
    : undefined;
  const isFormEditable = isOoApplicantIndividualFormEditable(caseData, sidebarContext.canEditDipAcceptedSections);
  const isDipAccepted = caseData?.status === ApplicationStatus.DIP_ACCEPTED;
  const existingIndividualsCount = caseData?.borrower.individuals.length ?? 0;
  const isFirstIndividual = isCreateMode
    ? existingIndividualsCount === 0
    : caseData?.borrower.individualOrder[0] === individualId;

  if (isFormEditable && !formValidationContext) {
    throw new Error('OO applicant individual form must be used within a FormValidationProvider when editable');
  }

  const setIsSubmitting = formValidationContext?.setIsSubmitting ?? (() => undefined);

  if ((isCaseLoading && !caseData) || !caseData) {
    return { isLoading: true as const };
  }

  if (!isCreateMode && (!individualId || !individualData)) {
    return { isLoading: true as const };
  }

  const schemaProps = {
    formatMessage,
    variant: 'oo' as const,
    isFormEditable,
    isDipAccepted,
    isDipAcceptedOrLater: isDipAccepted,
    showGrossIncome: false,
    hasCompanyApplicant: false,
    isFirstIndividual: Boolean(isFirstIndividual),
  };

  const formSchema = individualFormSchema(schemaProps);
  const adjustedFormSchema = isFormEditable ? formSchema : makeFormReadonly(formSchema);

  return {
    isLoading: false as const,
    isFormEditable,
    formKey: `${caseData.id}-${isCreateMode ? 'create' : individualId}`,
    footerProps: isFormEditable
      ? {
          submitLabel: formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.nextLabel' }),
        }
      : {},
    formProps: {
      columns: adjustedFormSchema,
      isFormEditable,
      name: isCreateMode ? 'oo_applicant_individual_create' : 'oo_applicant_individual',
      params: { caseId: caseData.id, individualId: individualId ?? 'create' },
      scrollToFirstError: true,
      request: async () => {
        if (isCreateMode || !individualId || !individualData) {
          return {};
        }

        const incomeValues = await btlApplicationFormApi.getIndividualIncomes(
          caseData.id,
          caseData.borrower.id,
          individualId,
        );
        const incomes = incomeValues?.incomes ?? individualData.incomes ?? [];

        return {
          ...mapIndividualToFormValues(individualData),
          ...incomeValues,
          incomes: normalizeIndividualIncomesForForm(incomes, individualData.incomes ?? []),
        };
      },
      onFinishFailed: () => {
        message.error(formatMessage({ id: 'common.error.requiredField' }));
      },
      onFinish: isFormEditable
        ? async (values: any) => {
            const {
              addressList,
              email,
              phoneNumber,
              previousNames,
              residencyStatus,
              relationshipToPrimaryApplicant,
              relationshipToSpv,
              incomes,
              otherIncome,
              ...rest
            } = values;

            const trimmedPhone = String(phoneNumber ?? '').trim();
            if (trimmedPhone && !isValidGbPhoneNumber(trimmedPhone)) {
              message.error(formatMessage({ id: 'common.error.validPhoneNumber' }));
              return;
            }

            let addressHistory = transformAddress(addressList);
            addressHistory = addressHistory.map((entry: AddressHistory, index: number) => {
              if (index === 0) {
                return { ...entry, isCurrentAddress: true, moveOutDate: null };
              }

              return { ...entry, isCurrentAddress: false };
            });

            const contactDetails = [
              email && { contact: email, contactType: 'EMAIL' },
              phoneNumber && { contact: phoneNumber, contactType: 'PHONE' },
            ].filter(Boolean);

            const isPrimaryApplicant = isCreateMode
              ? existingIndividualsCount === 0
              : Boolean(individualData?.isPrimary);

            const transformedValues: IndividualCreateDto = {
              ...rest,
              addressHistory,
              contactDetails,
              ...(previousNames?.length
                ? {
                    previousTitle: previousNames[0]?.title,
                    previousFirstName: previousNames[0]?.firstName,
                    previousMiddleName: previousNames[0]?.middleName,
                    previousLastName: previousNames[0]?.lastName,
                  }
                : {}),
              ...(residencyStatus?.status
                ? {
                    residencyStatus: {
                      ...residencyStatus,
                    },
                  }
                : { residencyStatus: { status: 'UNKNOWN', visaEndDate: null, dualNationality: null } }),
              isPrimary: isPrimaryApplicant,
              relationshipToPrimaryApplicant: isPrimaryApplicant ? null : relationshipToPrimaryApplicant,
              relationshipToSpv,
              isVulnerable: null,
              vulnerabilityCategory: null,
              vulnerabilityDetails: null,
              vulnerabilityDateIdentified: null,
            };

            const valuesWithIsPrimary = mapFormIncomesPayloadToDto(
              { incomes, otherIncome },
              individualData?.incomes ?? [],
            );

            try {
              setIsSubmitting(true);

              if (!caseData.borrower.id) {
                throw new Error('A Borrower id is required');
              }

              if (isCreateMode) {
                const createdIndividual = await btlApplicationFormApi.createIndividual(
                  caseData.id,
                  caseData.borrower.id,
                  transformedValues,
                );

                if (valuesWithIsPrimary.incomes?.length || valuesWithIsPrimary.otherIncome) {
                  await btlApplicationFormApi.createIndividualIncome(
                    caseData.id,
                    caseData.borrower.id,
                    createdIndividual.id,
                    valuesWithIsPrimary,
                  );
                }

                const households = await btlApplicationFormApi.getHouseholds(caseData.id);
                const existingHouseholdId = households?.[0]?.id;
                if (existingHouseholdId) {
                  await btlApplicationFormApi.linkIndividualToHousehold(
                    caseData.id,
                    caseData.borrower.id,
                    createdIndividual.id,
                    { householdId: existingHouseholdId },
                  );
                }

                await invalidateBtlApplicationData(queryClient, caseData.id);
                message.success(
                  `Individual added${env.VITE_ENV === 'production' ? '' : ` with id: ${createdIndividual.id}`}`,
                );
              } else {
                if (!individualId) {
                  throw new Error('Individual id is required');
                }

                await btlApplicationFormApi.editIndividual(
                  caseData.id,
                  caseData.borrower.id,
                  individualId,
                  transformedValues,
                );
                await btlApplicationFormApi.createIndividualIncome(
                  caseData.id,
                  caseData.borrower.id,
                  individualId,
                  valuesWithIsPrimary,
                );
                await invalidateBtlApplicationData(queryClient, caseData.id);
                await queryClient.invalidateQueries({
                  queryKey: ['btl-application-get-individual-incomes', caseData.id, caseData.borrower.id, individualId],
                });
              }

              navigate({ search: `?section=${CaseSidebarItemId.APPLICANTS}` });
            } catch (error: unknown) {
              console.error(error);
              message.error(error instanceof Error ? error.message : 'Failed to save applicant details');
            } finally {
              setIsSubmitting(false);
            }
          }
        : undefined,
    },
  };
}
